/**
 * Área VENDAS: o funil de leads (Kanban), o novo lead, a indicação e o ranking de quem indica.
 *
 * Cada função reproduz UMA chamada do legado (`portal/admin.html`), com a mesma tabela, as mesmas
 * colunas, o mesmo filtro e a mesma ordem, e o `admin.html:linha` ao lado. Quem decide o que volta
 * é o RLS (ADR 0002): nenhuma função aqui filtra por papel, porque o legado também não filtra.
 *
 * O QUE NÃO MORA AQUI: nenhuma regra de negócio. Qual status aceita o arrasto, quem pode mover,
 * quando a exclusão do pagamento é pedida, como o ranking conta: tudo isso é código puro em
 * `apps/admin/src/telas/vendas/regras.ts`, testável sem banco. Este módulo só traduz.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum (ver PADRAO.md).
import {
  clienteDe,
  executar,
  type FonteDeDados,
  type LinhaCuponsDesconto,
  type LinhaCuponsDescontoResgates,
  type LinhaGiftCards,
  type LinhaIndicadoresExternos,
  type LinhaLeadsFunil,
  type Resultado,
} from '../nucleo.js';

// ---------------------------------------------------------------------------------------------
// Leitura do quadro
// ---------------------------------------------------------------------------------------------

/**
 * Todos os leads, o mais novo primeiro. Legado: `admin.html:6510-6513`,
 * `.from('leads_funil').select('*').order('data_criacao', { ascending: false })`.
 *
 * A ordem vem do banco, e não da tela, porque é ela que decide a ordem dos cards em cada coluna:
 * o legado filtra por status e mantém a ordem de chegada (`admin.html:6583`).
 */
export function listarLeads(fonte: FonteDeDados): Promise<Resultado<LinhaLeadsFunil[]>> {
  return executar<LinhaLeadsFunil[]>(
    clienteDe(fonte).from('leads_funil').select('*').order('data_criacao', { ascending: false }),
  );
}

/**
 * Grava no lead o e-mail da cliente vinculada. Legado: `admin.html:6528`,
 * `.from('leads_funil').update({ email }).eq('id', lead.id)`.
 *
 * O legado dispara e NÃO espera (é a correção silenciosa de leads antigos, sem aviso na tela), e
 * por isso a tela chama com `void`. A função devolve `Resultado` mesmo assim, para ninguém
 * precisar de outra assinatura no dia em que alguém quiser esperar.
 */
export function corrigirEmailDoLead(fonte: FonteDeDados, leadId: number, email: string): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('leads_funil').update({ email }).eq('id', leadId));
}

/**
 * Todos os gift cards, o emitido mais recente primeiro. Legado: `admin.html:7149`,
 * `.from('gift_cards').select('*').order('emitido_em', { ascending: false })`.
 *
 * O Kanban precisa dele para os gift cards vigentes (novo lead e card) e para os já resgatados
 * por cada lead (`admin.html:6700`).
 */
export function listarGiftCards(fonte: FonteDeDados): Promise<Resultado<LinhaGiftCards[]>> {
  return executar<LinhaGiftCards[]>(
    clienteDe(fonte).from('gift_cards').select('*').order('emitido_em', { ascending: false }),
  );
}

/**
 * Os cupons, o criado mais recente primeiro. Legado: `admin.html:7324`,
 * `.from('cupons_desconto').select('*').order('criado_em', { ascending: false })`.
 */
export function listarCupons(fonte: FonteDeDados): Promise<Resultado<LinhaCuponsDesconto[]>> {
  return executar<LinhaCuponsDesconto[]>(
    clienteDe(fonte).from('cupons_desconto').select('*').order('criado_em', { ascending: false }),
  );
}

/**
 * Os resgates de cupom, sem ordem. Legado: `admin.html:7325`,
 * `.from('cupons_desconto_resgates').select('*')`. O card mostra o cupom que a cliente informou.
 */
export function listarResgatesDeCupons(fonte: FonteDeDados): Promise<Resultado<LinhaCuponsDescontoResgates[]>> {
  return executar<LinhaCuponsDescontoResgates[]>(clienteDe(fonte).from('cupons_desconto_resgates').select('*'));
}

/**
 * As pessoas de fora que indicam, por nome. Legado: `admin.html:7042`,
 * `.from('indicadores_externos').select('*').order('nome')`.
 */
export function listarIndicadoresExternos(fonte: FonteDeDados): Promise<Resultado<LinhaIndicadoresExternos[]>> {
  return executar<LinhaIndicadoresExternos[]>(
    clienteDe(fonte).from('indicadores_externos').select('*').order('nome'),
  );
}

/** As duas colunas de indicação de um lead, que é tudo o que o ranking lê. */
export type IndicacaoDoLead = Pick<LinhaLeadsFunil, 'indicado_por_user_id' | 'indicado_por_externo_id'>;

/**
 * Quem indicou cada lead, para o ranking "Quem mais indica". Legado: `admin.html:7557`,
 * `.from('leads_funil').select('indicado_por_user_id, indicado_por_externo_id')`.
 */
export function listarIndicacoesDosLeads(fonte: FonteDeDados): Promise<Resultado<IndicacaoDoLead[]>> {
  return executar<IndicacaoDoLead[]>(
    clienteDe(fonte).from('leads_funil').select('indicado_por_user_id, indicado_por_externo_id'),
  );
}

// ---------------------------------------------------------------------------------------------
// Escritas do lead
// ---------------------------------------------------------------------------------------------

/** O que o "Adicionar lead" grava, campo por campo, como `admin.html:7599-7606`. */
export interface NovoLead {
  readonly nome: string;
  readonly contato_whatsapp: string;
  readonly origem: string;
  readonly status: string;
  readonly observacoes: string;
  readonly indicado_por_user_id: string | null;
  // O tipo é largo de propósito: o legado grava aqui o resultado de `parseInt` (ver `regras.ts`).
  readonly indicado_por_externo_id: string | number | null;
}

/**
 * Cria o lead e devolve o id. Legado: `admin.html:7607`,
 * `.from('leads_funil').insert(registro).select('id').single()`.
 *
 * `.single()` é o certo AQUI, e só aqui: a linha acabou de ser criada, então ela existe, e a
 * ausência seria falha de verdade (recusa do RLS, por exemplo).
 */
export function criarLead(fonte: FonteDeDados, registro: NovoLead): Promise<Resultado<{ id: number }>> {
  return executar<{ id: number }>(clienteDe(fonte).from('leads_funil').insert(registro).select('id').single());
}

/**
 * Atualiza campos de um lead. Legado: `admin.html:6808` (marketing: origem e observações),
 * `admin.html:6843` (salvar do card), `admin.html:7010` e `7028` (status).
 *
 * UMA função para as quatro porque o legado faz a MESMA chamada nas quatro, só com outro corpo:
 * `.from('leads_funil').update(atualizacao).eq('id', lead.id)`.
 */
export function atualizarLead(
  fonte: FonteDeDados,
  leadId: number,
  campos: Record<string, unknown>,
): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('leads_funil').update(campos).eq('id', leadId));
}

/**
 * Apaga os compromissos do lead ANTES de apagar o lead. Legado: `admin.html:6883`,
 * `.from('compromissos_agenda').delete().eq('lead_id', lead.id)`.
 *
 * O PORQUÊ está no legado (`admin.html:6878-6882`): `lead_id` é ON DELETE SET NULL, e um
 * compromisso sem título ficaria com título E lead nulos, o que a constraint
 * `compromissos_agenda_titulo_ou_lead` recusa. Apagar primeiro evita a recusa.
 */
export function excluirCompromissosDoLead(fonte: FonteDeDados, leadId: number): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('compromissos_agenda').delete().eq('lead_id', leadId));
}

/** Apaga o lead. Legado: `admin.html:6885`, `.from('leads_funil').delete().eq('id', lead.id)`. */
export function excluirLead(fonte: FonteDeDados, leadId: number): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('leads_funil').delete().eq('id', leadId));
}

// ---------------------------------------------------------------------------------------------
// Indicação e gift card
// ---------------------------------------------------------------------------------------------

/**
 * Cadastra a pessoa de fora que indicou. Legado: `admin.html:7499`,
 * `.from('indicadores_externos').insert({ nome }).select().single()`.
 *
 * Quem decide se ela já existe (sem diferenciar caixa) é a tela, antes, pela lista carregada:
 * a mesma busca em memória do legado (`admin.html:7497`).
 */
export function criarIndicadorExterno(fonte: FonteDeDados, nome: string): Promise<Resultado<LinhaIndicadoresExternos>> {
  return executar<LinhaIndicadoresExternos>(
    clienteDe(fonte).from('indicadores_externos').insert({ nome }).select().single(),
  );
}

/**
 * Vincula um gift card a um lead (o resgate). Legado: `admin.html:6573`,
 * `.from('gift_cards').update({ resgatado_em, lead_id_resgate }).eq('id', id)`.
 *
 * A hora vem de quem chama, e não daqui: o legado usa `new Date().toISOString()` no momento do
 * clique, e a regra de "já é deste lead, pula" também é da tela (`admin.html:6571`).
 */
export function resgatarGiftCard(
  fonte: FonteDeDados,
  giftCardId: number,
  leadId: number,
  resgatadoEm: string,
): Promise<Resultado<null>> {
  return executar<null>(
    clienteDe(fonte)
      .from('gift_cards')
      .update({ resgatado_em: resgatadoEm, lead_id_resgate: leadId })
      .eq('id', giftCardId),
  );
}

// ---------------------------------------------------------------------------------------------
// O que o movimento de status lê ou apaga
// ---------------------------------------------------------------------------------------------

/**
 * A etapa da jornada da cliente NESTE lead. Legado: `admin.html:7023`,
 * `.from('jornada_cliente').select('etapa').eq('user_id', ...).eq('lead_id', ...).maybeSingle()`.
 *
 * `maybeSingle` porque a jornada pode legitimamente não existir: o legado trata a ausência como
 * "Diagnóstico Inicial" (`admin.html:7024`), e não como falha.
 */
export function lerEtapaDaJornada(
  fonte: FonteDeDados,
  userId: string,
  leadId: number,
): Promise<Resultado<{ etapa: string } | null>> {
  return executar<{ etapa: string } | null>(
    clienteDe(fonte)
      .from('jornada_cliente')
      .select('etapa')
      .eq('user_id', userId)
      .eq('lead_id', leadId)
      .maybeSingle(),
  );
}

/**
 * Apaga o plano de pagamento do lead e devolve o cupom aplicado a pendente. Legado:
 * `excluirPagamentoDoLead`, `admin.html:6953-6968`, nesta ordem:
 *   1. `pagamentos` `select('id').eq('lead_id').maybeSingle()` (6954); sem plano, para aqui;
 *   2. `parcelas` `delete().eq('pagamento_id')` (6956);
 *   3. `pagamentos` `delete().eq('id')` (6957);
 *   4. `cupons_desconto_resgates` o último aplicado do lead (6961-6963) e, havendo,
 *      `update({ aplicado_em: null, valor_aplicado: null })` (6965).
 *
 * OS ERROS SÃO IGNORADOS, como no legado: nenhuma das cinco chamadas lá confere `error`. Esta
 * função devolve `void` pelo mesmo motivo; inventar uma falha que o legado não mostra seria
 * mudar o comportamento na migração. É também a função que o Financeiro reaproveita (linha 112
 * do inventário), e por isso ela sai inteira daqui, e não em pedaços.
 */
export async function excluirPagamentoDoLead(fonte: FonteDeDados, leadId: number): Promise<void> {
  const sb = clienteDe(fonte);
  // O plano do lead, que pode não existir: `maybeSingle` devolve nulo sem erro.
  const plano = await executar<{ id: number } | null>(
    sb.from('pagamentos').select('id').eq('lead_id', leadId).maybeSingle(),
  );
  // Sem plano (ou falha na leitura, que o legado lê como "sem plano"), nada a apagar.
  if (!plano.ok || !plano.dados) return;
  // Parcelas antes do pagamento: é a ordem do legado, e a chave estrangeira pede isso.
  await executar<null>(sb.from('parcelas').delete().eq('pagamento_id', plano.dados.id));
  await executar<null>(sb.from('pagamentos').delete().eq('id', plano.dados.id));
  // O resgate de cupom aplicado MAIS RECENTE do lead, e só um (`limit(1)`), como o legado.
  const resgate = await executar<{ id: number } | null>(
    sb
      .from('cupons_desconto_resgates')
      .select('id')
      .eq('lead_id', leadId)
      .not('aplicado_em', 'is', null)
      .order('aplicado_em', { ascending: false })
      .limit(1)
      .maybeSingle(),
  );
  // Volta a pendente, senão o cupom some do registro de pagamento refeito (`admin.html:6958-6960`).
  if (resgate.ok && resgate.dados) {
    await executar<null>(
      sb.from('cupons_desconto_resgates').update({ aplicado_em: null, valor_aplicado: null }).eq('id', resgate.dados.id),
    );
  }
}

// ---------------------------------------------------------------------------------------------
// Abrir a cliente a partir do card
// ---------------------------------------------------------------------------------------------

/** O que o card precisa para abrir a ficha de uma cliente que não estava na lista carregada. */
export interface CadastroDaCliente {
  readonly user_id: string;
  readonly nome_completo: string | null;
  readonly email: string | null;
  readonly telefone: string | null;
}

/**
 * O questionário geral da cliente, quando ela não estava na lista carregada. Legado:
 * `admin.html:6941-6945`, `.from('questionario_geral').select('user_id, nome_completo, email,
 * telefone').eq('user_id', userId).maybeSingle()`.
 *
 * `maybeSingle` porque a cliente pode não ter respondido: o legado trata isso com a frase "Não
 * encontrei o cadastro dessa cliente no portal..." (`admin.html:6947`), e não como erro técnico.
 */
export function lerCadastroDaCliente(fonte: FonteDeDados, userId: string): Promise<Resultado<CadastroDaCliente | null>> {
  return executar<CadastroDaCliente | null>(
    clienteDe(fonte)
      .from('questionario_geral')
      .select('user_id, nome_completo, email, telefone')
      .eq('user_id', userId)
      .maybeSingle(),
  );
}
